/**
 * Utilidades para la **carga mayorista** de órdenes de venta (OV).
 *
 * - **Parseo:** lectura multi-hoja y detección de fila cabecera (Falabella Chile eOD, Falabella Perú distributions, Ripley, Paris, Oechsle, etc.).
 * - **Mapeo:** columnas Excel → filas con `pickField` / `pickFieldExact` (este último para tienda/cliente
 *   sin confundir con “SKU Cliente”).
 * - **Vista previa:** validación por línea y `groupBulkPreviewRowsByClient` para resumir por tienda (columna Cliente).
 */
import * as XLSX from 'xlsx';
import {
  getSapItemPriceByPriceListId,
  getSapItemStockForWarehouse,
  normalizeSapItemCodeKey
} from '../services/sapB1Items';
import { normalizeBulkExcelCellDisplay } from './bulkExcelDisplayFormat';
import { getWholesaleCountryFromSession } from './userSession';

/** Texto de cabecera / etiqueta: MAYÚSCULAS, sin tildes, espacios colapsados (lectura robusta ante mezcla de casos). */
export const normalizeCellKey = (k) =>
  String(k || '')
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/** Índice 0-based → letra de columna estilo Excel (A, B, …, Z, AA, …) */
export function indexToExcelColumnLetter(index) {
  let n = index + 1;
  let s = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/**
 * Solo la columna **PC13** (variantes de escritura de la cabecera) alimenta `skuIac` → consulta SAP.
 * Debe declararse antes de `scoreRowAsHeader` para priorizar la fila que realmente trae esa cabecera.
 */
/**
 * Cabeceras que identifican la columna PC13 en Excel (tras `normalizeCellKey` en mayúsculas).
 * Incluye variantes con espacio o guion porque el backend aún no unifica texto.
 */
const SKU_IAC_PC13_ALIASES = ['pc13', 'pc 13', 'pc-13', 'pc_13'];

/** @param {string} cell */
function normalizedCellMatchesPc13HeaderLabel(cell) {
  const n = normalizeCellKey(String(cell ?? '').trim());
  if (!n) return false;
  const compact = n.replace(/[\s_-]/g, '');
  if (compact === 'PC13') return true;
  return SKU_IAC_PC13_ALIASES.some((a) => normalizeCellKey(a) === n);
}

function trimTrailingEmptyRows(rows) {
  let end = rows.length;
  while (end > 0) {
    const r = rows[end - 1];
    if (!r || r.every((c) => !String(c ?? '').trim())) end -= 1;
    else break;
  }
  return rows.slice(0, end);
}

/**
 * Lee TODO el libro: cada hoja como matriz de strings (sin asumir encabezados ni columnas fijas).
 * Sirve para plantillas distintas: se ve exactamente lo que trae el archivo.
 *
 * @param {File} file
 * @returns {Promise<{ sheetNames: string[], grids: { name: string, rows: string[][], rowsRaw?: unknown[][], rowCount: number, colCount: number }[] }>}
 */
export async function parseSpreadsheetFileFull(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true, sheetStubs: true });
  const sheetNames = wb.SheetNames || [];
  const padRowToMax = (r, maxCols) => {
    const arr = Array.isArray(r) ? [...r] : [];
    while (arr.length < maxCols) arr.push('');
    return arr;
  };
  const grids = sheetNames.map((name) => {
    const sheet = wb.Sheets[name];
    const rowsFormatted = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
    const rowsRawIn = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: '',
      raw: true,
      cellDates: true
    });
    if (!rowsFormatted.length) {
      return { name, rows: [], rowsRaw: [], rowCount: 0, colCount: 0 };
    }
    const maxCols = Math.max(
      0,
      ...rowsFormatted.map((r) => (Array.isArray(r) ? r.length : 0)),
      ...rowsRawIn.map((r) => (Array.isArray(r) ? r.length : 0))
    );
    let paddedFmt = rowsFormatted.map((r) =>
      padRowToMax(r, maxCols).map((c) => normalizeBulkExcelCellDisplay(c))
    );
    let paddedRaw = rowsRawIn.map((r) => padRowToMax(r, maxCols));
    paddedFmt = trimTrailingEmptyRows(paddedFmt);
    paddedRaw = paddedRaw.slice(0, paddedFmt.length);
    const colCount = paddedFmt.length ? Math.max(...paddedFmt.map((r) => r.length)) : 0;
    return {
      name,
      rows: paddedFmt,
      rowsRaw: paddedRaw,
      rowCount: paddedFmt.length,
      colCount
    };
  });
  return { sheetNames, grids };
}

// --- Detección automática cabecera / datos (salta títulos e instrucciones) ---

const HEADER_HINTS = [
  'n° oc',
  'nº oc',
  'n oc',
  'numero de oc',
  'tienda',
  'destino',
  'sucursal',
  'sku',
  'descrip',
  'descripcion',
  'cant',
  'precio',
  'stock',
  'sap',
  'lista',
  'orden',
  'compra',
  'producto',
  'cantidad',
  'acord',
  'iac',
  'cliente',
  'ripley',
  'solicitado',
  'unitario',
  'departamento',
  'paris',
  'ordenes',
  'local destino',
  'fecha oc',
  'fecha creacion',
  'fecha generacion',
  'unidad',
  'nro_o',
  'fecha_emision',
  'descripcion_larga',
  'pc13'
];

/**
 * Puntuación de una fila como posible cabecera de tabla (no título largo).
 * @param {string[]} row
 * @returns {number} -1 si vacía; cuanto mayor, más probable cabecera
 */
export function scoreRowAsHeader(row) {
  if (!row || !row.length) return -1;
  const cells = row.map((c) => String(c ?? '').trim());
  const filled = cells.filter(Boolean);
  if (filled.length === 0) return -1;

  const maxLen = Math.max(...filled.map((f) => f.length));
  const sumLen = filled.join('').length;

  // Título / leyenda: pocas celdas y mucho texto
  if (filled.length <= 2 && (maxLen > 70 || sumLen > 140)) return 1;
  if (filled.length === 1 && maxLen > 45) return 2;

  const joined = normalizeCellKey(filled.join(' | '));
  let kwHits = 0;
  for (const h of HEADER_HINTS) {
    const nh = normalizeCellKey(h);
    if (nh && joined.includes(nh)) kwHits += 1;
  }

  let score = kwHits * 5 + filled.length * 2;
  if (filled.length >= 4 && filled.every((f) => f.length <= 42)) score += 8;
  if (maxLen > 60) score -= 12;
  if (kwHits >= 2 && filled.length >= 4) score += 10;
  if (filled.length >= 6 && kwHits >= 1) score += 4;

  /** Si una celda es literalmente "PC13" (o variante), casi seguro es la fila cabecera ancha del retail (col. AL, etc.). */
  if (cells.some((c) => normalizedCellMatchesPc13HeaderLabel(c))) {
    score += 28;
  }

  return score;
}

const MIN_HEADER_SCORE = 18;

/**
 * Índice 0-based de la fila cabecera, o null si no hay confianza suficiente.
 * @param {string[][]} rows
 * @param {number} [maxScan]
 */
export function detectHeaderRowIndex(rows, maxScan = 50) {
  if (!rows?.length) return null;
  let bestIdx = -1;
  let bestScore = -1;
  const limit = Math.min(rows.length, maxScan);
  for (let i = 0; i < limit; i += 1) {
    const s = scoreRowAsHeader(rows[i]);
    if (s < 0) continue;
    if (s > bestScore) {
      bestScore = s;
      bestIdx = i;
    }
  }
  if (bestIdx < 0 || bestScore < MIN_HEADER_SCORE) return null;
  return bestIdx;
}

/**
 * Primera fila con varias celdas con datos (fallback si no pasó el umbral de cabecera).
 * @param {string[][]} rows
 */
export function findFirstDataHeaderFallback(rows, maxScan = 40) {
  if (!rows?.length) return 0;
  for (let i = 0; i < Math.min(rows.length, maxScan); i += 1) {
    const n = rows[i].filter((c) => String(c ?? '').trim()).length;
    if (n >= 3) return i;
  }
  return 0;
}

/**
 * Convierte cabecera + filas en objetos { "Nombre columna": valor }
 * @param {string[]} headerRow
 * @param {string[][]} dataRows
 * @returns {Record<string, string>[]}
 */
export function structuredGridToObjects(headerRow, dataRows) {
  const keys = (headerRow || []).map((h, i) => {
    const t = String(h ?? '').trim();
    return t || `_col_${i + 1}`;
  });
  return dataRows.map((dataRow) => {
    const obj = {};
    keys.forEach((k, j) => {
      obj[k] = dataRow[j] ?? '';
    });
    return obj;
  });
}

/**
 * Filas de datos sin filas completamente vacías.
 * @param {string[][]} rows
 */
export function filterNonEmptyDataRows(rows) {
  return rows.filter((r) => r.some((c) => String(c ?? '').trim()));
}

/**
 * Alinea índices de fila en `grid.rows` (0-based) con `previewRows` en el mismo orden que
 * `buildValidatedRowsFromGrid` (filas de datos no vacías bajo la cabecera).
 *
 * @param {{ rows?: any[][] }} grid
 * @param {number} headerIndex - fila cabecera 0-based
 * @param {object[]} previewRows - filas mapeadas / enriquecidas de la vista previa
 * @returns {Map<number, { validationStatus: string, validationMessage: string }>}
 */
export function mapGridRowIndexToBulkValidation(grid, headerIndex, previewRows) {
  const rows = grid?.rows || [];
  const map = new Map();
  const hi = Number(headerIndex);
  if (!Number.isFinite(hi) || hi < 0 || !Array.isArray(previewRows)) return map;

  let pi = 0;
  for (let ri = hi + 1; ri < rows.length; ri += 1) {
    const r = rows[ri];
    if (!Array.isArray(r) || !r.some((c) => String(c ?? '').trim())) continue;
    const pr = previewRows[pi];
    pi += 1;
    if (pr) {
      map.set(ri, {
        validationStatus: pr.validationStatus || 'ok',
        validationMessage: String(pr.validationMessage ?? '')
      });
    }
  }
  return map;
}

export {
  bulkPreviewRowEligibleForCleanPriceStockExport,
  mapGridRowIndexToBulkCleanPriceStockExportEligible
} from './bulkPreviewExportEligibility';

const OECHSLE_PERU_PROFILE_ID = 'oechsle_peru_oc';
/** Ripley Perú — hoja INTERNATIONAL_APPAREL (1 OV a bodega central; destino tienda por línea). */
export const RIPLEY_PERU_PROFILE_ID = 'ripley_peru_international_apparel';
/** Oechsle Perú — OC nacional (.xls). */
export { OECHSLE_PERU_PROFILE_ID };

/** Plantilla mayorista IX Comercio (`ejemplo_IX_Comercio.xlsx`): sin N° OC; ítem SAP = columna «Número de artículo». */
export const IX_COMERCIO_PROFILE_ID = 'ix_comercio';

/** IX Comercio Perú: mismo Excel que Chile; se elige por país de la sesión (`@iacperu.com`). Moneda PEN. */
export const IX_COMERCIO_PERU_PROFILE_ID = 'ix_comercio_peru';

/** `true` para ambos perfiles IX Comercio (Chile y Perú): sin N° OC, fecha de entrega, «Número de artículo». */
export function isIxComercioProfileId(profileId) {
  return profileId === IX_COMERCIO_PROFILE_ID || profileId === IX_COMERCIO_PERU_PROFILE_ID;
}

/**
 * Vista previa Perú que resuelve el ítem SAP por catálogo del cliente
 * (AlternateCatNum), no por el PC13 del Excel.
 * Falabella Perú, Oechsle y Ripley Perú. IX Comercio y Multimarca quedan afuera.
 */
export function usesCustomerCatalogSubstitute(profileId) {
  return (
    profileId === RIPLEY_PERU_PROFILE_ID ||
    profileId === OECHSLE_PERU_PROFILE_ID ||
    profileId === FALABELLA_PERU_DISTRIBUTIONS_PROFILE_ID
  );
}

/** Plantilla mayorista Multimarca: sin N° OC en Excel (folio `{idFiscal}-{correlativo}`); detección por RutCliente + Indicador de impuestos. */
export const MULTIMARCA_PROFILE_ID = 'multimarca';

/** Multimarca Perú: mismo Excel que Chile; se elige por país de la sesión. Moneda PEN, IGV 18 %. */
export const MULTIMARCA_PERU_PROFILE_ID = 'multimarca_peru';

/** `true` para ambos perfiles Multimarca (Chile y Perú): sin N° OC, RUT del cliente en el archivo. */
export function isMultimarcaProfileId(profileId) {
  return profileId === MULTIMARCA_PROFILE_ID || profileId === MULTIMARCA_PERU_PROFILE_ID;
}

/** Export Falabella Perú «distributions details» (p. ej. `FALABELLA-oc-*-distributions-details-*.xlsx`). */
export const FALABELLA_PERU_DISTRIBUTIONS_PROFILE_ID = 'falabella_peru_distributions';

/**
 * Lee N° orden y fecha del bloque superior de plantillas Oechsle Perú (`.xls` «OC nacional», p. ej. `OE-Orden_N°_*_hrs.xls`).
 * Busca etiquetas `Número de Orden:` / `Fecha:` en cualquier celda de las filas anteriores a la cabecera de detalle.
 */
export function extractOechslePeruPreamble(rows, headerIndex) {
  const out = { nOc: '', fechaOc: '', enviarA: '' };
  const end = Math.max(0, Math.min(Number(headerIndex) || 0, rows?.length ?? 0));
  for (let ri = 0; ri < end; ri += 1) {
    const row = rows[ri];
    if (!Array.isArray(row)) continue;
    for (let ci = 0; ci < row.length; ci += 1) {
      const labelRaw = String(row[ci] ?? '').trim();
      if (!labelRaw) continue;
      const key = normalizeCellKey(labelRaw).replace(/:+\s*$/g, '').trim();
      const next = String(row[ci + 1] ?? '').trim();
      if ((key === 'NUMERO DE ORDEN' || key.startsWith('NUMERO DE ORDEN')) && next) {
        const compact = next.replace(/\s+/g, '');
        if (/^[\d-]+$/.test(compact)) out.nOc = compact;
      }
      if (key === 'FECHA' && next) {
        out.fechaOc = next;
      }
      if ((key === 'ENVIAR A' || key.startsWith('ENVIAR A')) && next) {
        out.enviarA = next;
      }
    }
  }
  return out;
}

/**
 * Perú Ripley / Oechsle: vista previa con 1 OV a bodega central y destino por tienda en cada línea.
 * @param {string} [profileId]
 * @returns {boolean}
 */
export function showsPeruCentralWarehousePreview(profileId) {
  const pid = String(profileId || '').trim();
  return pid === RIPLEY_PERU_PROFILE_ID || pid === OECHSLE_PERU_PROFILE_ID;
}

/**
 * Etiqueta de la columna agrupadora en vista previa mayorista.
 * @param {string} [profileId]
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function getBulkGroupingColumnLabel(profileId, t) {
  const pid = String(profileId || '').trim();
  if (pid === RIPLEY_PERU_PROFILE_ID) return t('Suc Entrega');
  if (pid === OECHSLE_PERU_PROFILE_ID) return t('Enviar a:');
  if (pid === FALABELLA_PERU_DISTRIBUTIONS_PROFILE_ID) return t('Lugar de entrega');
  return t('Sucursal Destino');
}

/**
 * Etiqueta de destino por línea en detalle expandido; `null` si no aplica.
 * @param {string} [profileId]
 * @param {(key: string) => string} t
 * @returns {string|null}
 */
export function getBulkLineDestinationLabel(profileId, t) {
  const pid = String(profileId || '').trim();
  if (pid === RIPLEY_PERU_PROFILE_ID) return t('Suc Destino');
  if (pid === OECHSLE_PERU_PROFILE_ID) return t('Sucursal');
  return null;
}

/** Combina código + descripción de sucursal (p. ej. Ripley PE «Cod Suc Entrega» + «Suc Entrega»). */
function combinePeruBranchLabel(code, desc) {
  const c = String(code ?? '').trim();
  const d = String(desc ?? '').trim();
  if (c && d) return `${c} - ${d}`;
  return d || c || '';
}

/**
 * Etiquetas típicas de export eOD / B2B Falabella: la fecha de la OC va en el bloque superior,
 * no repetida en cada línea del detalle (por eso `Fecha OC` quedaba en —).
 * @param {string[][]} rows
 * @param {number} headerIndex
 * @returns {{ nOc?: string, fechaOc?: string }}
 */
export function extractFalabellaPreamble(rows, headerIndex) {
  const out = { nOc: '', fechaOc: '' };
  const end = Math.max(0, Math.min(Number(headerIndex) || 0, rows?.length ?? 0));

  const isFechaOcLabel = (key) => {
    if (!key || key.length < 4) return false;
    if (key === 'FECHA') return true;
    if (!key.includes('FECHA')) return false;
    // Fecha de entrega ≠ fecha de creación/emisión de la OC
    if (
      key.includes('FECHA ENTREGA') &&
      !key.includes('CREACION') &&
      !key.includes('GENERACION') &&
      !key.includes('EMISION') &&
      !key.includes('DOCUMENTO')
    ) {
      return false;
    }
    return (
      key.includes('CREACION') ||
      key.includes('GENERACION') ||
      key.includes('EMISION') ||
      key.includes('INGRESO') ||
      key.includes('DOCUMENTO') ||
      (key.includes('ORDEN') && key.includes('COMPRA'))
    );
  };

  for (let ri = 0; ri < end; ri += 1) {
    const row = rows[ri];
    if (!Array.isArray(row)) continue;
    for (let ci = 0; ci < row.length; ci += 1) {
      const labelRaw = String(row[ci] ?? '').trim();
      if (!labelRaw) continue;
      const key = normalizeCellKey(labelRaw).replace(/:+\s*$/g, '').trim();
      let val = String(row[ci + 1] ?? '').trim();
      if (!val && ci + 2 < row.length) {
        val = String(row[ci + 2] ?? '').trim();
      }
      if (!val) continue;

      if (
        (key === 'FECHA_EMISION' || key === 'FECHA_EMISION_' || isFechaOcLabel(key)) &&
        !out.fechaOc
      ) {
        out.fechaOc = val;
      }

      const compact = val.replace(/\s+/g, '');
      if (
        key === 'NRO_O' ||
        (key.includes('ORDEN') && key.includes('COMPRA') && /\d/.test(compact)) ||
        (key.includes('NUMERO') && key.includes('OC')) ||
        key === 'N° OC' ||
        key === 'N OC' ||
        key.startsWith('N° ORDEN') ||
        key.startsWith('N ORDEN')
      ) {
        if (/^[\d-]+$/.test(compact) && compact.length >= 4 && !out.nOc) {
          out.nOc = val;
        }
      }
    }
  }
  return out;
}

/**
 * Primer RUT no vacío de la columna RutCliente (Excel Multimarca) para mostrar en cabecera de vista previa.
 * @param {{ rows?: string[][] }} grid
 * @param {number} headerIndex - fila cabecera 0-based
 * @returns {string}
 */
export function extractMultimarcaRutClienteFromGrid(grid, headerIndex) {
  const rows = grid?.rows;
  if (!rows?.length) return '';
  const hi = Number(headerIndex);
  if (!Number.isFinite(hi) || hi < 0 || hi >= rows.length) return '';
  const headerRow = rows[hi] || [];
  let colIdx = -1;
  for (let i = 0; i < headerRow.length; i += 1) {
    const key = normalizeCellKey(headerRow[i]);
    if (key === 'RUTCLIENTE' || key.includes('RUTCLIENTE')) {
      colIdx = i;
      break;
    }
  }
  if (colIdx < 0) return '';
  for (let r = hi + 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (!Array.isArray(row)) continue;
    if (row.every((c) => !String(c ?? '').trim())) continue;
    const v = String(row[colIdx] ?? '').trim();
    if (v) return v;
  }
  return '';
}

/**
 * A partir de la rejilla de UNA hoja: detecta cabecera, omite preámbulo y devuelve objetos + metadatos.
 * @param {{ rows: string[][] }} grid
 * @param {(s: string) => string} t
 * @param {{ profileId?: string, headerIndex?: number, priceMap?: Map<string, number>|null, brandDiscountPercent?: number }} [options] - `headerIndex` forzado tras detección multi-hoja; `profileId` para alias y validación; `priceMap` (solo Falabella Chile) para resolver precio desde eOC; `brandDiscountPercent` (solo Falabella Chile) para aplicar descuento de marca al precio del eOC.
 */
export function buildValidatedRowsFromGrid(grid, t, options = {}) {
  const profileId = options.profileId || BULK_FORMAT_DEFAULT_ID;
  if (!grid?.rows?.length) {
    return {
      rows: [],
      headerIndex: 0,
      usedDetection: false,
      preambleRows: 0,
      profileId
    };
  }
  const { rows } = grid;
  let headerIdx =
    typeof options.headerIndex === 'number' &&
    options.headerIndex >= 0 &&
    options.headerIndex < rows.length
      ? options.headerIndex
      : detectHeaderRowIndex(rows);
  let usedDetection =
    typeof options.headerIndex === 'number' ? true : headerIdx != null;
  if (headerIdx == null) {
    headerIdx = findFirstDataHeaderFallback(rows);
    usedDetection = false;
  }
  const headerRow = rows[headerIdx] || [];
  const dataRows = filterNonEmptyDataRows(rows.slice(headerIdx + 1));
  const objects = structuredGridToObjects(headerRow, dataRows);
  let preambleMeta;
  if (profileId === OECHSLE_PERU_PROFILE_ID) {
    preambleMeta = extractOechslePeruPreamble(rows, headerIdx);
  } else if (profileId === FALABELLA_CHILE_PROFILE_ID || profileId === FALABELLA_GENERIC_LEGACY_ID) {
    preambleMeta = extractFalabellaPreamble(rows, headerIdx);
  }
  const mapped = mapAndValidateRows(objects, t, {
    profileId,
    preambleMeta,
    priceMap: options.priceMap,
    brandDiscountPercent: options.brandDiscountPercent
  });
  return {
    rows: mapped,
    headerIndex: headerIdx,
    usedDetection,
    preambleRows: headerIdx,
    profileId
  };
}

function normalizeRowKeys(raw) {
  const out = {};
  Object.keys(raw).forEach((k) => {
    out[normalizeCellKey(k)] = raw[k];
  });
  return out;
}

/** Busca valor por alias sobre fila con claves ya normalizadas */
function pickField(normRow, aliases) {
  for (const alias of aliases) {
    const a = normalizeCellKey(alias);
    if (Object.prototype.hasOwnProperty.call(normRow, a) && normRow[a] !== '' && normRow[a] != null) {
      return normRow[a];
    }
  }
  // Segunda pasada: la cabecera contiene el alias (cabeceras largas). No usar `a.includes(key)`:
  // p. ej. alias "fecha oc cliente" haría coincidir la columna "Cliente" y rellenaría fecha con el nombre de tienda.
  for (const key of Object.keys(normRow)) {
    for (const alias of aliases) {
      const a = normalizeCellKey(alias);
      if (!a) continue;
      if (key === a || key.includes(a)) {
        const v = normRow[key];
        if (v !== '' && v != null) return v;
      }
    }
  }
  return '';
}

/**
 * Solo coincidencia exacta de cabecera normalizada (evita que "cliente" tome la columna "sku cliente").
 */
function pickFieldExact(normRow, aliases) {
  for (const alias of aliases) {
    const a = normalizeCellKey(alias);
    if (Object.prototype.hasOwnProperty.call(normRow, a) && normRow[a] !== '' && normRow[a] != null) {
      return normRow[a];
    }
  }
  return '';
}

export function parseMoneyNumber(value) {
  if (value === '' || value == null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  const raw = String(value)
    .trim()
    .replace(/S\//gi, '')
    .replace(/\$/g, '')
    .replace(/\s/g, '');
  // Coma y punto juntos: el último separador es el decimal ("1,234.50" → 1234.5; "1.234,50" → 1234.5)
  if (raw.includes(',') && raw.includes('.')) {
    const decimalIsComma = raw.lastIndexOf(',') > raw.lastIndexOf('.');
    const normalized = decimalIsComma ? raw.replace(/\./g, '').replace(',', '.') : raw.replace(/,/g, '');
    const n = parseFloat(normalized.replace(/[^\d.-]/g, ''));
    return Number.isNaN(n) ? null : n;
  }
  // Miles solo con coma (ej. Paris/CL: "44,990" → 44990)
  if (/^\d{1,3}(,\d{3})+$/.test(raw)) {
    const n = parseFloat(raw.replace(/,/g, ''), 10);
    return Number.isNaN(n) ? null : n;
  }
  // Miles solo con punto (ej. "44.990")
  if (/^\d{1,3}(\.\d{3})+$/.test(raw)) {
    const n = parseFloat(raw.replace(/\./g, ''), 10);
    return Number.isNaN(n) ? null : n;
  }
  // Decimal con punto (ej. soles "199.9"); "44.990" ya quedó arriba como miles.
  if (/^\d+\.\d{1,2}$/.test(raw) && !raw.includes(',')) {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? null : n;
  }
  const s = raw
    .replace(/\./g, '')
    .replace(/,/g, '.')
    .replace(/[^\d.-]/g, '');
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

export function parseIntLoose(value) {
  if (value === '' || value == null) return null;
  if (typeof value === 'number' && !Number.isNaN(value)) return Math.floor(value);
  const n = parseInt(String(value).replace(/\D/g, ''), 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * Quita hora/minutos al final de un string de fecha (p. ej. Excel `3/16/26 0:00` o `17/03/2026 13:23:33`).
 * @param {string} s
 */
function stripTrailingTimeFromDateString(s) {
  const t = String(s).trim();
  if (!t) return '';
  return t
    .replace(/\s+\d{1,2}:\d{2}(:\d{2})?(\.\d+)?(\s*[AaPp][Mm])?\s*$/i, '')
    .trim();
}

/** Año de 2 dígitos → 4 (OC recientes en siglo XXI). */
function fullYearFromTwoDigit(yy) {
  const n = parseInt(String(yy), 10);
  if (!Number.isFinite(n) || n < 0 || n > 99) return null;
  return n + (n < 50 ? 2000 : 1900);
}

/**
 * Vista previa OV: siempre `dd/mm/aaaa` con ceros (ej. `04/03/2026`), componentes **locales** (sin UTC).
 * @param {Date} d
 */
function formatFechaOcEsCl(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Fecha estable para vista previa mayorista: `Date`, serial Excel o texto (ISO, DD-MM-YYYY Chile, DD/MM/YYYY,
 * y cortos `m/d/yy` típicos de Excel en-US al exportar con `raw: false`).
 */
export function normalizeFechaOcDisplay(raw) {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return formatFechaOcEsCl(raw);
  }
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 200 && raw < 600000) {
    const dc = XLSX.SSF?.parse_date_code?.(raw);
    if (dc && dc.y) {
      return formatFechaOcEsCl(new Date(dc.y, dc.m - 1, dc.d));
    }
  }
  let s = stripTrailingTimeFromDateString(String(raw).trim());
  if (!s) return '';

  // ISO `YYYY-MM-DD` o inicio de ISO con hora (sin interpretar como UTC)
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const d = parseInt(m[3], 10);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d) {
      return formatFechaOcEsCl(dt);
    }
  }

  // DD-MM-YYYY o D-M-YYYY (export Falabella Chile `04-03-2026`)
  m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (m) {
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d) {
      return formatFechaOcEsCl(dt);
    }
  }

  // DD/MM/YYYY o D/M/YYYY (año 4 cifras) → día/mes/año Chile
  m = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
  if (m) {
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d) {
      return formatFechaOcEsCl(dt);
    }
  }

  // M/D/YY o MM/DD/YY (Excel `raw: false`, locale en-US): mes / día / año
  m = s.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{2})$/);
  if (m) {
    const month = parseInt(m[1], 10);
    const day = parseInt(m[2], 10);
    const yFull = fullYearFromTwoDigit(m[3]);
    if (yFull != null && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      const dt = new Date(yFull, month - 1, day);
      if (dt.getFullYear() === yFull && dt.getMonth() === month - 1 && dt.getDate() === day) {
        return formatFechaOcEsCl(dt);
      }
    }
  }

  return s;
}

/**
 * Columna de tienda / cliente destino (misma noción que "Cliente" en la lista OV: TDOMIN, TARICA, etc.).
 * Orden: nombres específicos primero; "cliente" solo con match exacto vía pickFieldExact.
 */
const TIENDA_O_CLIENTE_ALIASES_BASE = [
  'sucursal de destino',
  'tienda destino',
  'tienda dest',
  'destino tienda',
  'nombre tienda destino',
  'tienda',
  'destino',
  'nombre tienda',
  'desc tienda',
  'codigo tienda',
  'cod tienda',
  'cod. tienda',
  'local',
  'local destino',
  'sucursal',
  'nombre sucursal',
  'punto de venta',
  'punto venta',
  'pv',
  'cliente',
  'nombre cliente',
  'cliente destino',
  'destino cliente',
  'razon social cliente',
  'razón social cliente',
  'tienda cliente',
  'desc cliente',
  'descripcion cliente',
  'descripción cliente'
];

const ALIASES_BASE = {
  nOc: ['n° oc', 'nº oc', 'n oc', 'numero oc', 'número de oc', 'numero de oc', 'orden compra', 'oc'],
  tiendaDestino: TIENDA_O_CLIENTE_ALIASES_BASE,
  skuCliente: ['sku cliente', 'sku client', 'sku'],
  skuIac: [...SKU_IAC_PC13_ALIASES],
  descripcion: ['descripcion', 'descripción', 'producto'],
  cantOc: ['unidad', 'unidades', 'cant oc', 'cantidad oc', 'cantidad', 'cant'],
  /** Fecha de la orden de compra (texto o serial Excel ya normalizado al leer la celda). */
  fechaOc: [
    'fecha creacion oc',
    'fecha creación oc',
    'fecha de creacion oc',
    'fecha de creación oc',
    'fecha creacion',
    'fecha creación',
    'fecha de creacion',
    'fecha de creación',
    'fecha generacion oc',
    'fecha generación oc',
    'fecha generacion',
    'fecha generación',
    'fecha emision oc',
    'fecha emisión oc',
    'fecha ingreso oc',
    'fecha ingreso',
    'fecha oc',
    'fecha de oc',
    'fecha orden',
    'fecha de orden',
    'fecha pedido',
    'fecha de pedido',
    'fecha emision',
    'fecha emisión',
    'fecha documento',
    'fecha de documento',
    'fecha oc cliente',
    'order date',
    'fecha despacho',
    'fecha de despacho'
  ],
  precioAcordado: ['precio acordado', 'precio acord'],
  precioListaSap: ['precio lista sap', 'precio sap', 'lista sap'],
  stockDispSap: ['stock disp sap', 'stock sap', 'stock disp', 'stock disponible', 'stock'],
  /** Código de catálogo del cliente (Substitute de AlternateCatNum). Vacío si el perfil no lo usa. */
  catalogSubstitute: []
};

/** Id del perfil Falabella Chile (export eOD); usado tanto en detección como en lógica especial del eOC. */
export const FALABELLA_CHILE_PROFILE_ID = 'falabella_chile';

/** Perfil por defecto: export eOD Falabella **Chile** (cabeceras fijas; no es plantilla “genérica” multi-país). */
export const BULK_FORMAT_DEFAULT_ID = FALABELLA_CHILE_PROFILE_ID;

/**
 * Diferencia máxima en CLP entre precio acordado (Excel/eOC) y precio lista SAP **solo cuando hay
 * descuento por marca aplicado** (porcentaje > 0). Redondeos distintos ≤ este valor se tratan como
 * coincidencia. Sin descuento (0) la validación es estricta (margen 0): cualquier diferencia es discrepancia.
 */
export const BULK_PREVIEW_SAP_PRICE_TOLERANCE_CLP = 5;

/** Id. heredado; se resuelve a `falabella_chile` en `profileById`. */
export const FALABELLA_GENERIC_LEGACY_ID = 'falabella_generic';

/**
 * @typedef {object} BulkFormatProfileDef
 * @property {string} id
 * @property {string} labelKey - clave corta para UI (ej. Ripley)
 * @property {number} specificity - desempate: mayor = más específico
 * @property {string[]} headerMarks - substrings normalizadas que deben aparecer en la fila cabecera
 * @property {string[]} [requiredHeaderMarks] - todas deben estar presentes o el perfil puntúa 0 (p. ej. Multimarca exige RutCliente)
 * @property {Partial<Record<keyof ALIASES_BASE, string[]>>} [aliasOverrides] - listas que se anteponen a ALIASES_BASE
 * @property {boolean} [strictProfile] - si true: solo `aliasOverrides` por campo (sin mezclar sinónimos genéricos de `ALIASES_BASE`)
 * @property {string[]} [tiendaAliasesExtra] - solo este perfil (se anteponen a tienda; con `strictProfile`, sustituye la lista de tienda)
 * @property {{ code: string, locale: string, labelKey: string }} [currency] - ISO 4217 + locale `Intl` + texto UI (traducible)
 * @property {{ skipStrictSapPrice?: boolean }} [validation] - `skipStrictSapPrice`: si `true`, omite comparar precio Excel vs lista SAP; por defecto (`{}`) todos los perfiles validan como Chile (solo advertencia si difiere; no bloquea la carga).
 * @property {number} [sapPriceListId] - **Legacy / no usado en enrich:** la lista SAP sale del cliente en SAP (`PriceListNum`), no del perfil.
 */

/** Moneda por defecto de la vista previa mayorista (Chile). */
export const BULK_DEFAULT_CURRENCY = {
  code: 'CLP',
  locale: 'es-CL',
  labelKey: 'Pesos chilenos (CLP)'
};

/** @type {BulkFormatProfileDef[]} */
const BULK_FORMAT_PROFILES = [
  /**
   * Oechsle Perú — OC nacional (hoja `OC`): preámbulo con `Número de Orden:` / `Fecha:`; detalle con columnas literales del portal.
   * @see OE-Orden_N°_*_*_hrs.xls (cabecera fila tipo: Oechsle SKU … PC13, TALLA; costo unitario en «Costo»).
   */
  {
    id: 'oechsle_peru_oc',
    labelKey: 'Oechsle',
    specificity: 120,
    strictProfile: true,
    /** Solo subcadenas distintivas del export OE (evitar «costo» en «Centro Costo», «tipo» en «Tipo de orden», etc.). */
    headerMarks: [
      'oechsle sku',
      'descripcion del producto',
      'style',
      'material handling',
      'tipo de curva',
      'ratio de curva',
      'costo total',
      'codigo ean',
      'sucursal',
      'unidades'
    ],
    /** Sin tiendaAliasesExtra: agrupador OV = `enviarA` del preámbulo; destino por línea en columna «Sucursal». */
    currency: { code: 'PEN', locale: 'es-PE', labelKey: 'Soles (S/)' },
    aliasOverrides: {
      /** N° OC, fecha y bodega central (`enviarA`) vienen del preámbulo; destino tienda por línea en «Sucursal». */
      nOc: [],
      skuCliente: ['Oechsle SKU'],
      skuIac: [...SKU_IAC_PC13_ALIASES],
      descripcion: ['Descripción del producto'],
      cantOc: ['Unidades'],
      fechaOc: [],
      /** Costo unitario (alinea con «Costo Total» = Unidades × Costo). */
      precioAcordado: ['Costo'],
      precioListaSap: [],
      stockDispSap: [],
      catalogSubstitute: ['Oechsle SKU']
    },
    validation: {}
  },
  /**
   * Multimarca: sin N° OC en Excel (backend autogenera MM01…); RutCliente en archivo se ignora en servidor.
   * Detección: RutCliente + Indicador de impuestos (diferencia de IX Comercio). Sucursal literal; sin fecha de entrega.
   */
  {
    id: MULTIMARCA_PROFILE_ID,
    labelKey: 'Multimarca',
    specificity: 118,
    strictProfile: true,
    currency: { code: 'CLP', locale: 'es-CL', labelKey: 'Pesos chilenos (CLP)' },
    headerMarks: [
      'rutcliente',
      'indicador de impuestos',
      'sucursal',
      'numero de articulo',
      'precio por unidad',
      'cantidad'
    ],
    /** Alineado con backend: MULTIMARCA solo si el Excel trae RutCliente (IX no la trae). */
    requiredHeaderMarks: ['rutcliente', 'indicador de impuestos'],
    tiendaAliasesExtra: ['sucursal'],
    aliasOverrides: {
      nOc: [],
      skuCliente: [],
      skuIac: ['número de artículo'],
      descripcion: ['descripción'],
      cantOc: ['cantidad'],
      fechaOc: [],
      precioAcordado: ['precio por unidad'],
      precioListaSap: [],
      stockDispSap: []
    },
    sapPriceListId: 3, // legacy: no usado; lista SAP = canal de venta
    validation: {}
  },
  /**
   * Multimarca Perú: mismo Excel que Chile (misma firma de cabecera). Solo compite en sesiones de
   * Perú (`country`) y tiene `specificity` mayor para ganarle al perfil chileno; moneda soles.
   */
  {
    id: MULTIMARCA_PERU_PROFILE_ID,
    labelKey: 'Multimarca Perú',
    specificity: 119,
    strictProfile: true,
    country: 'PE',
    currency: { code: 'PEN', locale: 'es-PE', labelKey: 'Soles (S/)' },
    headerMarks: [
      'rutcliente',
      'indicador de impuestos',
      'sucursal',
      'numero de articulo',
      'precio por unidad',
      'cantidad'
    ],
    requiredHeaderMarks: ['rutcliente', 'indicador de impuestos'],
    tiendaAliasesExtra: ['sucursal'],
    aliasOverrides: {
      nOc: [],
      skuCliente: [],
      skuIac: ['número de artículo'],
      descripcion: ['descripción'],
      cantOc: ['cantidad'],
      fechaOc: [],
      precioAcordado: ['precio por unidad'],
      precioListaSap: [],
      stockDispSap: []
    },
    validation: {}
  },
  /**
   * IX Comercio: sin N° OC en Excel (backend genera orden); detección por Sucursal + Número de artículo + Precio por unidad
   * (+ opcional «Fecha de entrega» en plantilla Retail, p. ej. `Ejemplo_carga_masiva_Retail_*_IXComercio.xlsx`).
   * Consulta SAP con «Número de artículo» (no hay PC13). Cabeceras adicionales según ejemplo IX / Retail.
   */
  {
    id: IX_COMERCIO_PROFILE_ID,
    labelKey: 'IX Comercio',
    specificity: 115,
    strictProfile: true,
    currency: { code: 'CLP', locale: 'es-CL', labelKey: 'Pesos chilenos (CLP)' },
    headerMarks: ['sucursal', 'numero de articulo', 'precio por unidad', 'fecha de entrega'],
    tiendaAliasesExtra: ['sucursal'],
    aliasOverrides: {
      nOc: [],
      skuCliente: [],
      skuIac: ['número de artículo'],
      descripcion: ['descripción'],
      cantOc: ['cantidad'],
      fechaOc: [
        'fecha de entrega',
        'fecha entrega',
        'fecha entrega estimada',
        'fecha de entrega estimada',
        'entrega estimada',
        'fecha programada de entrega'
      ],
      precioAcordado: ['precio por unidad'],
      precioListaSap: [],
      stockDispSap: []
    },
    /** Lista de precios SAP B1 para comparar con el Excel (resto de formatos usan lista 1). */
    sapPriceListId: 3, // legacy: no usado; lista SAP = canal de venta
    validation: {}
  },
  /**
   * IX Comercio Perú: mismo Excel que IX Comercio Chile. Solo compite en sesiones de Perú (`country`)
   * y tiene `specificity` mayor para ganarle al perfil chileno; moneda soles con 2 decimales.
   */
  {
    id: IX_COMERCIO_PERU_PROFILE_ID,
    labelKey: 'IX Comercio Perú',
    specificity: 116,
    strictProfile: true,
    country: 'PE',
    currency: { code: 'PEN', locale: 'es-PE', labelKey: 'Soles (S/)' },
    headerMarks: ['sucursal', 'numero de articulo', 'precio por unidad', 'fecha de entrega'],
    tiendaAliasesExtra: ['sucursal'],
    aliasOverrides: {
      nOc: [],
      skuCliente: [],
      skuIac: ['número de artículo'],
      descripcion: ['descripción'],
      cantOc: ['cantidad'],
      fechaOc: [
        'fecha de entrega',
        'fecha entrega',
        'fecha entrega estimada',
        'fecha de entrega estimada',
        'entrega estimada',
        'fecha programada de entrega'
      ],
      precioAcordado: ['precio por unidad'],
      precioListaSap: [],
      stockDispSap: []
    },
    validation: {}
  },
  /**
   * Ripley Perú — hoja `INTERNATIONAL_APPAREL_CORPORATI` (cabeceras literales del xlsx portal, p. ej. `RIPLEY-INTERNATIONAL_APPAREL_*`).
   * Distinto a Ripley Chile (`Cod.Art. Ripley`, `Sucursal de Destino`, `Solicitado (u)`): aquí `Cod Art Ripley`, `Suc Destino`, `Suc Entrega`, `Cant Solicitada`, etc.
   */
  {
    id: RIPLEY_PERU_PROFILE_ID,
    labelKey: 'Ripley Perú',
    specificity: 110,
    strictProfile: true,
    headerMarks: [
      'numero oc',
      'suc destino',
      'suc entrega',
      'cant solicitada',
      'cod art ripley',
      'cod art venta',
      'desc art ripley',
      'precio unitario',
      'costo base unitario',
      'costo neto unitario',
      'cod art proveedor (case pack)',
      'dimension 1',
      'fecha generacion',
      'pc13'
    ],
    /** Agrupador OV = bodega central (Suc Entrega); destino tienda por línea en Suc Destino (ver `mapAndValidateRows`). */
    tiendaAliasesExtra: ['Suc Entrega', 'Cod Suc Entrega'],
    aliasOverrides: {
      nOc: ['Numero OC'],
      skuCliente: ['Cod Art Venta'],
      skuIac: [...SKU_IAC_PC13_ALIASES],
      descripcion: ['Desc Art Ripley'],
      cantOc: ['Cant Solicitada'],
      /** Comparación SAP: únicamente «Costo Neto Unitario» (no «Precio Unitario» retail). */
      precioAcordado: ['Costo Neto Unitario'],
      precioListaSap: [],
      stockDispSap: [],
      fechaOc: ['Fecha Generacion'],
      catalogSubstitute: ['Cod Art Ripley']
    },
    currency: { code: 'PEN', locale: 'es-PE', labelKey: 'Soles (S/)' },
    validation: {}
  },
  /**
   * Ripley Chile — export `International_Apparel_Corporati` (cabeceras fijas del xlsx retail).
   * `strictProfile`: solo estas columnas; sin mezclar alias genéricos de otras plantillas.
   */
  {
    id: 'ripley_international_apparel',
    labelKey: 'Ripley',
    specificity: 100,
    strictProfile: true,
    currency: { code: 'CLP', locale: 'es-CL', labelKey: 'Pesos chilenos (CLP)' },
    headerMarks: [
      'numero de oc',
      'sucursal de destino',
      'cod.art. ripley',
      'desc.art.ripley',
      'solicitado',
      'costo base unitario',
      'costo neto unitario',
      'precio unitario',
      'pc13',
      'fecha generacion'
    ],
    tiendaAliasesExtra: ['sucursal de destino'],
    aliasOverrides: {
      nOc: ['número de oc'],
      skuCliente: ['cod.art. venta', 'cod.art. ripley'],
      skuIac: ['pc13'],
      descripcion: ['desc.art.ripley'],
      cantOc: ['solicitado (u)'],
      /** Precio acordado: «Costo Neto Unitario» (precio negociado), no «Costo Base Unitario» ni «Precio Unitario» retail. */
      precioAcordado: ['costo neto unitario'],
      /** Lo rellena `enrichBulkPreviewRowsWithSapItems` desde SAP; sin columna Excel. */
      precioListaSap: [],
      stockDispSap: ['recepcionado (u)'],
      fechaOc: ['fecha generacion']
    },
    validation: {}
  },
  /**
   * Paris: hoja típica `ORDENES_DE_COMPRA` (export proveedor); `Hoja1` suele ser pivot/resumen y se ignora por nombre.
   * @see Orden N°417098 Paris.xls
   */
  {
    id: 'paris_ordenes_compra',
    labelKey: 'Paris',
    specificity: 90,
    headerMarks: [
      'n° orden',
      'sku paris',
      'local destino',
      'cod. prod. prov.',
      'pc13',
      'solicitado',
      'precio costo',
      'costo neto'
    ],
    tiendaAliasesExtra: ['local destino'],
    aliasOverrides: {
      nOc: ['n° orden', 'nº orden', 'numero de orden', 'n orden'],
      /** SKU en catálogo Paris (columna típica "SKU Paris" / "sku paris"). */
      skuCliente: ['sku paris'],
      /**
       * PC13 va a SAP (`skuIac`): solo columna PC13 (mismo criterio que Ripley/Falabella).
       * No usar «Cód. Prod. Prov.» (código interno proveedor); si falta PC13, `skuIac` queda vacío.
       */
      descripcion: ['descripción', 'descripcion'],
      cantOc: ['solicitado'],
      /** Precio acordado: «Costo Neto» (precio negociado), no «Precio Costo». */
      precioAcordado: ['costo neto'],
      /** Lista / referencia vs costo negociado */
      precioListaSap: ['precio normal'],
      stockDispSap: ['recibido'],
      /** En export proveedor Paris la fecha suele ir en "Emitida". */
      fechaOc: ['emitida', 'fecha orden', 'fecha oc', 'fecha emision', 'fecha emisión']
    },
    currency: { code: 'CLP', locale: 'es-CL', labelKey: 'Pesos chilenos (CLP)' },
    validation: {}
  },
  /**
   * Falabella **Perú** — export «distributions details» (cabeceras literales del portal; sin sinónimos extra).
   * Comparte con Chile la noción de **SKU**, **Local** / destino, **PC13** → SAP y columnas de cantidad/precio propias del layout Perú.
   * @see FALABELLA-oc-*-distributions-details-*.xlsx
   */
  {
    id: FALABELLA_PERU_DISTRIBUTIONS_PROFILE_ID,
    labelKey: 'Falabella Perú',
    specificity: 96,
    strictProfile: true,
    currency: { code: 'PEN', locale: 'es-PE', labelKey: 'Soles (S/)' },
    /** Marcas = textos que aparecen en la fila cabecera real (tras `normalizeCellKey` en mayúsculas). */
    headerMarks: [
      'numero oc',
      'fecha de emision',
      'unidades compradas',
      'descripcion lugar de entrega',
      'id de local',
      'modelo',
      'pc13',
      'sku',
      'costo'
    ],
    tiendaAliasesExtra: ['Local', 'Descripción lugar de entrega'],
    aliasOverrides: {
      nOc: ['Número OC'],
      skuCliente: ['SKU'],
      skuIac: [...SKU_IAC_PC13_ALIASES],
      descripcion: ['Modelo'],
      cantOc: ['Unidades compradas'],
      fechaOc: ['Fecha de emisión'],
      precioAcordado: ['COSTO'],
      precioListaSap: [],
      stockDispSap: [],
      catalogSubstitute: ['SKU']
    },
    validation: {}
  },
  /**
   * Falabella **Chile** — export eOD (ej. `eOD40520506 Falabella.xlsx`): cabeceras técnicas fijas.
   * Variantes mínimas: tildes / guion bajo en `FECHA_EMISION_` / mayúsculas (todo se normaliza).
   * @see columnas: NRO_O, FECHA_EMISION(_), SKU, LOCAL, DESCRIPCION_LARGA, UNIDADI, precio, …
   */
  {
    id: FALABELLA_CHILE_PROFILE_ID,
    labelKey: 'Falabella Chile',
    specificity: 85,
    strictProfile: true,
    currency: { code: 'CLP', locale: 'es-CL', labelKey: 'Pesos chilenos (CLP)' },
    headerMarks: [
      'nro_o',
      'fecha_emision',
      'sku',
      'local',
      'unidadi',
      'descripcion_larga',
      'precio',
      'pc13'
    ],
    tiendaAliasesExtra: ['local', 'nrd_loc', 'nrd loc'],
    aliasOverrides: {
      nOc: ['nro_o', 'nro o', 'numero oc', 'número oc', 'n° oc', 'nº oc'],
      skuCliente: ['sku'],
      /** Solo PC13 (cabecera eOD / variantes) → `integration/sapb1/items`. */
      skuIac: [...SKU_IAC_PC13_ALIASES],
      descripcion: ['descripcion_larga', 'descripción larga', 'descripcion', 'descripción'],
      cantOc: ['unidadi', 'unidad', 'unidades'],
      fechaOc: ['fecha_emision', 'fecha_emision_', 'fecha emision', 'fecha emisión'],
      precioAcordado: ['precio'],
      precioListaSap: [],
      stockDispSap: []
    },
    validation: {}
  }
];

function profileById(id) {
  const resolved = id === FALABELLA_GENERIC_LEGACY_ID ? FALABELLA_CHILE_PROFILE_ID : id;
  return (
    BULK_FORMAT_PROFILES.find((p) => p.id === resolved) ||
    BULK_FORMAT_PROFILES.find((p) => p.id === BULK_FORMAT_DEFAULT_ID)
  );
}

/**
 * @param {string} profileId
 * @returns {typeof ALIASES_BASE}
 */
export function mergeAliasesForProfile(profileId) {
  const p = profileById(profileId);
  const fieldKeys = [
    'nOc',
    'skuCliente',
    'skuIac',
    'descripcion',
    'cantOc',
    'fechaOc',
    'precioAcordado',
    'precioListaSap',
    'stockDispSap',
    'catalogSubstitute'
  ];

  if (p?.strictProfile) {
    const out = { ...ALIASES_BASE };
    const tiendaExtra = p.tiendaAliasesExtra || [];
    out.tiendaDestino = tiendaExtra.length ? [...tiendaExtra] : [...ALIASES_BASE.tiendaDestino];
    const ov = p.aliasOverrides || {};
    for (const k of fieldKeys) {
      if (Object.prototype.hasOwnProperty.call(ov, k)) {
        out[k] = [...(ov[k] || [])];
      }
    }
    return out;
  }

  const tiendaExtra = p?.tiendaAliasesExtra || [];
  const tiendaDestino = [...new Set([...tiendaExtra, ...ALIASES_BASE.tiendaDestino])];
  const out = { ...ALIASES_BASE, tiendaDestino };
  for (const k of fieldKeys) {
    const extra = p?.aliasOverrides?.[k];
    if (extra?.length) {
      out[k] = [...new Set([...extra, ...ALIASES_BASE[k]])];
    }
  }
  return out;
}

/**
 * Puntuación de cabecera frente a un perfil (marcas presentes × 10 + specificity si al menos 2 marcas).
 * @param {string[]} headerRow
 * @param {BulkFormatProfileDef} profile
 */
export function scoreHeaderAgainstProfile(headerRow, profile) {
  const joined = normalizeCellKey(
    (headerRow || [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean)
      .join(' | ')
  );
  if (!joined || !profile.headerMarks?.length) return 0;
  if (profile.requiredHeaderMarks?.length) {
    for (const req of profile.requiredHeaderMarks) {
      const reqNorm = normalizeCellKey(req);
      if (!reqNorm || !joined.includes(reqNorm)) return 0;
    }
  }
  let hits = 0;
  for (const m of profile.headerMarks) {
    const nm = normalizeCellKey(m);
    if (nm && joined.includes(nm)) hits += 1;
  }
  if (hits === 0) return 0;
  const minHitsForBonus = Math.max(2, Math.min(4, profile.headerMarks.length));
  const bonus = hits >= minHitsForBonus ? profile.specificity : 0;
  return hits * 10 + bonus;
}

/**
 * Elige el perfil que mejor encaja con una fila de cabecera.
 * @param {string[]} headerRow
 * @returns {{ profileId: string, score: number }}
 */
export function matchBestProfileForHeaderRow(headerRow) {
  let best = { profileId: BULK_FORMAT_DEFAULT_ID, score: 0 };
  /** Perfiles con `country` (p. ej. IX Comercio Perú) solo compiten si coincide el país de la sesión. */
  const sessionCountry = getWholesaleCountryFromSession();
  for (const p of BULK_FORMAT_PROFILES) {
    if (p.country && p.country !== sessionCountry) continue;
    const s = scoreHeaderAgainstProfile(headerRow, p);
    /**
     * Empate: gana el perfil del país de la sesión. Pasa cuando el Excel no trae todas las marcas y no
     * se suma `specificity` (p. ej. IX Comercio sin «Fecha de entrega» → IX Chile e IX Perú empatan).
     */
    const winsTieByCountry = s > 0 && s === best.score && Boolean(p.country);
    if (s > best.score || winsTieByCountry) best = { profileId: p.id, score: s };
  }
  return best;
}

/**
 * Analiza una hoja: mejor fila cabecera + perfil asociado.
 * @param {{ rows?: string[][] }} grid
 * @returns {{ profileId: string, headerIndex: number, matchScore: number }}
 */
export function analyzeGridForBulkPreview(grid) {
  const rows = grid?.rows;
  if (!rows?.length) {
    return { profileId: BULK_FORMAT_DEFAULT_ID, headerIndex: 0, matchScore: 0 };
  }
  let bestCombined = -1;
  let bestHi = 0;
  let bestProfileId = BULK_FORMAT_DEFAULT_ID;
  const limit = Math.min(rows.length, 40);
  for (let hi = 0; hi < limit; hi += 1) {
    const row = rows[hi];
    if (!Array.isArray(row)) continue;
    const filled = row.filter((c) => String(c ?? '').trim()).length;
    if (filled < 4) continue;
    const sr = scoreRowAsHeader(row);
    const { profileId, score: profScore } = matchBestProfileForHeaderRow(row);
    const combined = (sr >= 0 ? sr : 0) + profScore;
    if (combined > bestCombined) {
      bestCombined = combined;
      bestHi = hi;
      bestProfileId = profileId;
    }
  }
  if (bestCombined < 0) {
    bestHi = findFirstDataHeaderFallback(rows);
    const fb = matchBestProfileForHeaderRow(rows[bestHi] || []);
    bestProfileId = fb.profileId;
    bestCombined = fb.score;
  }
  return {
    profileId: bestProfileId,
    headerIndex: bestHi,
    matchScore: bestCombined
  };
}

/**
 * Hojas cuyo nombre no se usa como principal (alineado con backend: se descarta "Hoja1").
 * @param {string} [name]
 */
export function isBulkSheetNameIgnored(name) {
  const n = String(name || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '');
  return n === 'hoja1';
}

/**
 * Entre varias hojas, elige índice de hoja con mejor encaje (cabecera + volumen de datos).
 * Ignora por defecto la hoja llamada "Hoja1" si existe otra hoja con datos (misma regla que el backend).
 * @param {{ rows: string[][], name?: string, colCount?: number }[]} grids
 * @returns {{ gridIndex: number, profileId: string, headerIndex: number, matchScore: number }}
 */
export function pickBestGridForBulkPreview(grids) {
  if (!grids?.length) {
    return { gridIndex: 0, profileId: BULK_FORMAT_DEFAULT_ID, headerIndex: 0, matchScore: 0 };
  }
  const withRows = grids
    .map((grid, gi) => ({ grid, gi }))
    .filter(({ grid }) => grid?.rows?.length);
  if (!withRows.length) {
    return { gridIndex: 0, profileId: BULK_FORMAT_DEFAULT_ID, headerIndex: 0, matchScore: 0 };
  }
  const notIgnored = withRows.filter(({ grid }) => !isBulkSheetNameIgnored(grid.name));
  const toEvaluate = notIgnored.length ? notIgnored : withRows;

  let best = {
    gridIndex: toEvaluate[0].gi,
    profileId: BULK_FORMAT_DEFAULT_ID,
    headerIndex: 0,
    matchScore: -1
  };
  toEvaluate.forEach(({ grid, gi }) => {
    const a = analyzeGridForBulkPreview(grid);
    const headerIdx = a.headerIndex;
    const dataRows = filterNonEmptyDataRows((grid.rows || []).slice(headerIdx + 1));
    const richness = dataRows.length * 1000 + (grid.colCount || 0);
    const combined = a.matchScore + richness * 1e-6;
    if (combined > best.matchScore) {
      best = {
        gridIndex: gi,
        profileId: a.profileId,
        headerIndex: headerIdx,
        matchScore: combined
      };
    }
  });
  return best;
}

export function getBulkFormatLabelKey(profileId) {
  return profileById(profileId)?.labelKey ?? 'Falabella Chile';
}

/**
 * Moneda asociada al perfil de plantilla (vista previa / importes).
 * @param {string} profileId
 * @returns {{ code: string, locale: string, labelKey: string }}
 */
export function getBulkFormatCurrency(profileId) {
  const c = profileById(profileId)?.currency;
  if (c?.code && c?.locale && c?.labelKey) {
    return { code: c.code, locale: c.locale, labelKey: c.labelKey };
  }
  return { ...BULK_DEFAULT_CURRENCY };
}

/**
 * Redondeo de montos según moneda: CLP a entero; soles (y otras monedas) a 2 decimales.
 * @param {number | null | undefined} value
 * @param {string} currencyCode
 * @returns {number | null | undefined}
 */
export function roundMoneyForCurrency(value, currencyCode) {
  if (value == null || !Number.isFinite(value)) return value;
  return currencyCode === 'CLP' ? Math.round(value) : Math.round(value * 100) / 100;
}

/**
 * Formatea un importe numérico según la moneda del perfil (S/ soles con 2 decimales vs $ CLP entero).
 * @param {number | null | undefined} value
 * @param {string} profileId
 * @returns {string}
 */
export function formatBulkMoneyAmount(value, profileId) {
  if (value == null || (typeof value === 'number' && Number.isNaN(value))) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  const { code, locale } = getBulkFormatCurrency(profileId);
  const rounded = roundMoneyForCurrency(n, code);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: code === 'CLP' ? 0 : 2,
      maximumFractionDigits: code === 'CLP' ? 0 : 2
    }).format(rounded);
  } catch {
    if (code === 'PEN') {
      return `S/ ${rounded.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${Math.round(rounded).toLocaleString('es-CL')}`;
  }
}

/**
 * @param {File} file
 * @returns {Promise<object[]>} filas crudas como objetos { key: value }
 */
export async function parseSpreadsheetFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return [];
  const sheet = wb.Sheets[sheetName];
  return XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
}

/**
 * Parsea el archivo de precios eOC de Falabella Chile (Excel paralelo al eOD).
 * Reusa `parseSpreadsheetFileFull` para pasar por el mismo padding/normalización
 * que el archivo principal; devuelve las hojas listas para `buildPriceMapFromEocGrids`.
 *
 * @param {File} file
 * @returns {Promise<{ sheetNames: string[], grids: { name: string, rows: string[][], rowCount: number, colCount: number }[] }>}
 */
export async function parseEocPriceFile(file) {
  return parseSpreadsheetFileFull(file);
}

/**
 * Construye el mapa SKU → COSTO_UNI a partir de las hojas del eOC (Falabella Chile).
 * El eOC trae cabecera en las primeras filas con columnas `SKU` y `COSTO_UNI` (la real
 * usada por el negocio; `PRECIO_UNI` se ignora por diseño). Si hay duplicados, gana el primer SKU.
 *
 * Además detecta `CANTIDAD_PROD` y `TOT_DESCUENT` cuando están presentes y devuelve
 * `eocLines` (una línea por SKU único) para validar consistencia del descuento de marca.
 *
 * @param {{ rows?: string[][], name?: string }[]} grids - hojas devueltas por `parseEocPriceFile`
 * @returns {{
 *   priceMap: Map<string, number>,
 *   sheetUsed: string,
 *   gridIndex: number,
 *   headerIndex: number,
 *   skuCol: number,
 *   costoCol: number,
 *   cantidadCol: number,
 *   totDescentCol: number,
 *   totalSkus: number,
 *   nroOcDetected: string,
 *   eocLines: { sku: string, costo: number, cantidad: number|null, totDescent: number|null }[]
 * }}
 */
export function buildPriceMapFromEocGrids(grids) {
  const empty = {
    priceMap: new Map(),
    sheetUsed: '',
    gridIndex: -1,
    headerIndex: -1,
    skuCol: -1,
    costoCol: -1,
    cantidadCol: -1,
    totDescentCol: -1,
    totalSkus: 0,
    nroOcDetected: '',
    eocLines: []
  };
  if (!Array.isArray(grids) || grids.length === 0) return empty;

  const skuKey = normalizeCellKey('SKU');
  const costoKey = normalizeCellKey('COSTO_UNI');
  const nroOcKey = normalizeCellKey('NRO_OC');
  const cantidadKey = normalizeCellKey('CANTIDAD_PROD');
  const totDescentKey = normalizeCellKey('TOT_DESCUENT');

  for (let gi = 0; gi < grids.length; gi += 1) {
    const grid = grids[gi];
    const rows = grid?.rows;
    if (!Array.isArray(rows) || rows.length === 0) continue;

    let headerIdx = -1;
    let skuCol = -1;
    let costoCol = -1;
    let nroOcCol = -1;
    let cantidadCol = -1;
    let totDescentCol = -1;
    const scanLimit = Math.min(rows.length, 8);
    for (let i = 0; i < scanLimit; i += 1) {
      const row = rows[i];
      if (!Array.isArray(row)) continue;
      let s = -1;
      let c = -1;
      let n = -1;
      let q = -1;
      let t = -1;
      for (let ci = 0; ci < row.length; ci += 1) {
        const k = normalizeCellKey(row[ci]);
        if (s < 0 && k === skuKey) s = ci;
        if (c < 0 && k === costoKey) c = ci;
        if (n < 0 && k === nroOcKey) n = ci;
        if (q < 0 && k === cantidadKey) q = ci;
        if (t < 0 && k === totDescentKey) t = ci;
      }
      if (s >= 0 && c >= 0) {
        headerIdx = i;
        skuCol = s;
        costoCol = c;
        nroOcCol = n;
        cantidadCol = q;
        totDescentCol = t;
        break;
      }
    }

    if (headerIdx < 0) continue;

    const priceMap = new Map();
    /** Una entrada por SKU único (gana la primera ocurrencia, igual que `priceMap`). */
    const eocLines = [];
    let nroOcDetected = '';
    for (let i = headerIdx + 1; i < rows.length; i += 1) {
      const row = rows[i];
      if (!Array.isArray(row)) continue;
      const skuRaw = row[skuCol];
      const sku = String(skuRaw == null ? '' : skuRaw).trim();
      if (!sku) continue;
      const costo = parseMoneyNumber(row[costoCol]);
      if (costo == null || !Number.isFinite(costo) || costo <= 0) continue;
      if (!priceMap.has(sku)) {
        priceMap.set(sku, costo);
        const cantidadRaw = cantidadCol >= 0 ? parseMoneyNumber(row[cantidadCol]) : null;
        const totDescentRaw = totDescentCol >= 0 ? parseMoneyNumber(row[totDescentCol]) : null;
        eocLines.push({
          sku,
          costo,
          cantidad: Number.isFinite(cantidadRaw) ? cantidadRaw : null,
          totDescent: Number.isFinite(totDescentRaw) ? totDescentRaw : null
        });
      }
      if (!nroOcDetected && nroOcCol >= 0) {
        const oc = String(row[nroOcCol] == null ? '' : row[nroOcCol]).trim();
        if (oc) nroOcDetected = oc;
      }
    }

    if (priceMap.size > 0) {
      return {
        priceMap,
        sheetUsed: String(grid.name || ''),
        gridIndex: gi,
        headerIndex: headerIdx,
        skuCol,
        costoCol,
        cantidadCol,
        totDescentCol,
        totalSkus: priceMap.size,
        nroOcDetected,
        eocLines
      };
    }
  }

  return empty;
}

/**
 * Indica si las hojas parseadas corresponden a un maestro eOC de Falabella Chile
 * (presencia de columnas SKU + COSTO_UNI con al menos un precio válido).
 *
 * @param {{ rows?: string[][], name?: string }[]|null|undefined} grids
 * @returns {boolean}
 */
export function isFalabellaChileEocWorkbookGrids(grids) {
  return buildPriceMapFromEocGrids(grids || []).priceMap.size > 0;
}

/**
 * Valida que el `discountPercent` del partner SAP coincida con los importes `TOT_DESCUENT`
 * presentes en el eOC de Falabella Chile.
 *
 * Para cada línea del eOC con `cantidad` y `totDescent` definidos:
 *   esperado  = totDescent
 *   calculado = costo × (discountPercent / 100) × cantidad
 *   |esperado − calculado| ≤ tolerancia (1 unidad por defecto) ⇒ línea consistente.
 *
 * Se omite silenciosamente cuando:
 *   - `discountPercent` es 0/no numérico (no hay descuento que validar).
 *   - `eocLines` no aporta `cantidad` ni `totDescent` (eOC sin esas columnas).
 *
 * @param {{ sku: string, costo: number, cantidad: number|null, totDescent: number|null }[]} eocLines
 * @param {number} discountPercent Porcentaje (0-100) del partner SAP.
 * @param {{ tolerance?: number, maxMismatches?: number }} [options]
 * @returns {{ valid: boolean, mismatches: { sku: string, expected: number, calculated: number, diff: number }[], checked: number }}
 */
export function validateEocDiscountConsistency(eocLines, discountPercent, options = {}) {
  const tolerance = Number.isFinite(options.tolerance) && options.tolerance >= 0 ? options.tolerance : 1;
  const maxMismatches = Number.isFinite(options.maxMismatches) && options.maxMismatches > 0 ? options.maxMismatches : 5;
  const pct = Number(discountPercent);
  if (!Number.isFinite(pct) || pct <= 0) {
    return { valid: true, mismatches: [], checked: 0 };
  }
  if (!Array.isArray(eocLines) || eocLines.length === 0) {
    return { valid: true, mismatches: [], checked: 0 };
  }

  const factor = pct / 100;
  const mismatches = [];
  let checked = 0;

  for (const line of eocLines) {
    if (!line) continue;
    /** Saltar líneas sin cantidad o sin totDescent (eOC sin esas columnas o valores no parseables). */
    if (line.cantidad == null || line.totDescent == null) continue;
    const costo = Number(line.costo);
    const cantidad = Number(line.cantidad);
    const expected = Number(line.totDescent);
    if (!Number.isFinite(costo) || !Number.isFinite(cantidad) || !Number.isFinite(expected)) continue;
    checked += 1;
    const calculated = costo * factor * cantidad;
    const diff = Math.abs(calculated - expected);
    if (diff > tolerance && mismatches.length < maxMismatches) {
      mismatches.push({
        sku: String(line.sku || ''),
        expected,
        calculated,
        diff
      });
    }
  }

  return { valid: mismatches.length === 0, mismatches, checked };
}

/**
 * Reglas de validación precio SAP / stock / ítem no encontrado (vista previa carga mayorista).
 * Precio acordado vs lista SAP **con descuento de marca aplicado**: en **CLP**, si hay descuento (> 0) no hay discrepancia cuando |Δ| ≤ {@link BULK_PREVIEW_SAP_PRICE_TOLERANCE_CLP}; sin descuento (0) la validación es estricta (|Δ| = 0). En otras monedas se mantiene |Δ| ≤ 1.
 * El precio objetivo se calcula como `precioListaSap × (1 − brandDiscountPercent/100)` redondeado.
 *
 * @param {{ precioAcordado?: number|null, precioListaSap?: number|null, stockDispSap?: number|null, cantOc?: number|null, skuIac?: string }} row
 * @param {(key: string) => string} t
 * @param {{ skipStrictSap: boolean, sapItemLookupFailed?: boolean, profileId?: string, stockShortageIsBlocking?: boolean, sapWhsCodeMissing?: boolean, sapWarehouseRowMissing?: boolean, sessionWhsCode?: string, brandDiscountPercent?: number, sapPriceListMissing?: boolean }} opts
 * @param opts.stockShortageIsBlocking Si true (solo «Procesar seleccionados» → SAP): ítem/bodega no encontrados bloquean; precio distinto bloquea; stock ≤0 o &lt;40% del pedido bloquea; stock parcial ≥40% es advertencia. Si false (vista previa Excel): ítem/stock/precio como advertencias salvo `skipStrictSap`.
 * @param opts.brandDiscountPercent Porcentaje de descuento por marca del BusinessPartner SAP (0-100). Si > 0 se aplica al `precioListaSap` para calcular el precio objetivo contra el cual se compara `precioAcordado`. Default 0 (sin descuento, comportamiento legado).
 * @param opts.sapPriceListMissing Si true y la línea tiene SKU: error explícito (el cliente no tiene lista de precios asignada en SAP, `PriceListNum`). Nunca se compara contra una lista por defecto.
 * @returns {{ validationStatus: string, validationMessage: string, sapPriceMismatch: boolean }}
 */
export function computeBulkRowSapValidation(row, t, opts) {
  const skipStrictSap = opts.skipStrictSap;
  const sapItemLookupFailed = Boolean(opts.sapItemLookupFailed);
  const sapWhsCodeMissing = Boolean(opts.sapWhsCodeMissing);
  const sapWarehouseRowMissing = Boolean(opts.sapWarehouseRowMissing);
  const sessionWhsCode = String(opts.sessionWhsCode || '').trim();
  const profileId = opts.profileId || BULK_FORMAT_DEFAULT_ID;
  const currencyCode = getBulkFormatCurrency(profileId).code;
  /** Descuento por marca del partner SAP; aplica solo si es un número > 0. */
  const dPctRaw = Number(opts.brandDiscountPercent);
  const safeBrandDiscountPct = Number.isFinite(dPctRaw) && dPctRaw > 0 ? dPctRaw : 0;
  /**
   * CLP: hasta ±{@link BULK_PREVIEW_SAP_PRICE_TOLERANCE_CLP} solo si hay descuento por marca (> 0);
   * sin descuento, validación estricta (margen 0). Otras monedas: mismo criterio previo (≤1 unidad).
   */
  const sapPriceToleranceUnits =
    currencyCode === 'CLP'
      ? (safeBrandDiscountPct > 0 ? BULK_PREVIEW_SAP_PRICE_TOLERANCE_CLP : 0)
      : 1;
  /** Modo estricto envío SAP (listado OV): precio distinto, stock grave (&lt;40% o ≤0), cantidad inválida → error. Vista previa Excel: false. */
  const stockShortageIsBlocking = Boolean(opts.stockShortageIsBlocking);
  const precioAcordado = row.precioAcordado;
  const precioListaSap = row.precioListaSap;
  const stockDispSap = row.stockDispSap;
  const cantOc = row.cantOc;
  const skuIac = String(row.skuIac || '').trim();
  /**
   * Precio objetivo contra el que se valida, sin IGV: precio de lista × (1 − % descuento)
   * (CLP entero; soles con 2 decimales, como `totalLinea`).
   */
  const precioListaSapConDescuento =
    precioListaSap != null && Number.isFinite(precioListaSap)
      ? roundMoneyForCurrency(precioListaSap * (1 - safeBrandDiscountPct / 100), currencyCode)
      : null;

  let validationStatus = 'ok';
  let validationMessage = t('✓ OK para crear');
  let sapPriceMismatch = false;

  if (sapWhsCodeMissing && skuIac) {
    const whsMsg = t('Falta código de bodega (whsCode) en su usuario. Vuelva a iniciar sesión o contacte al administrador.');
    if (stockShortageIsBlocking) {
      validationStatus = 'error';
      validationMessage = '⚠ ' + whsMsg;
    } else {
      validationStatus = 'warning';
      validationMessage = t('⚠ ') + whsMsg;
    }
    return { validationStatus, validationMessage, sapPriceMismatch };
  }

  /**
   * Sin lista de precios SAP del cliente (`PriceListNum` del socio de negocio): error explícito
   * (nunca se compara contra una lista por defecto).
   * Siempre error (vista previa y envío) y solo en líneas con SKU.
   */
  if (opts.sapPriceListMissing && skuIac) {
    validationStatus = 'error';
    validationMessage =
      '⚠ ' +
      t('El cliente no tiene lista de precios asignada en SAP.');
    return { validationStatus, validationMessage, sapPriceMismatch };
  }

  if (sapItemLookupFailed && skuIac) {
    if (stockShortageIsBlocking) {
      validationStatus = 'error';
      validationMessage = '⚠ ' + t('Ítem SAP no encontrado:') + ` ${skuIac}`;
      return { validationStatus, validationMessage, sapPriceMismatch };
    }
    validationStatus = 'warning';
    validationMessage = t('⚠ Ítem SAP no encontrado:') + ` ${skuIac}`;
    return { validationStatus, validationMessage, sapPriceMismatch };
  }

  if (sapWarehouseRowMissing && skuIac) {
    const rowMsg =
      t('No hay fila de stock en SAP para la bodega') +
      (sessionWhsCode ? ` ${sessionWhsCode}.` : '.');
    if (stockShortageIsBlocking) {
      validationStatus = 'error';
      validationMessage = '⚠ ' + rowMsg;
    } else {
      validationStatus = 'warning';
      validationMessage = t('⚠ ') + rowMsg;
    }
    return { validationStatus, validationMessage, sapPriceMismatch };
  }

  const priceMismatch =
    !skipStrictSap &&
    precioAcordado != null &&
    precioListaSapConDescuento != null &&
    Math.abs(precioAcordado - precioListaSapConDescuento) > sapPriceToleranceUnits;

  const stockInsufficient =
    cantOc != null && stockDispSap != null && cantOc > stockDispSap;

  const chunks = [];
  if (priceMismatch) {
    sapPriceMismatch = true;
    /** Mostrar el precio neto (con descuento aplicado) que se uso para comparar. */
    const precioFmt = formatBulkMoneyAmount(precioListaSapConDescuento, profileId);
    chunks.push(t('Precio distinto') + ` (${precioFmt})`);
  }
  if (stockInsufficient) {
    chunks.push(t('Stock insuficiente') + `: pedido ${cantOc}, disp. ${stockDispSap}`);
  }

  const cantNum = Number(cantOc);
  const stockNum = stockDispSap != null ? Number(stockDispSap) : NaN;

  /** Envío SAP: precio distinto bloquea. */
  const sapPriceBlocks = stockShortageIsBlocking && priceMismatch;

  /**
   * Envío SAP: cantidad pedida inválida; stock no cubre el 40% del pedido; o stock SAP ≤ 0.
   * Stock parcial con ratio ≥ 40% no entra aquí (solo advertencia vía `chunks`).
   */
  let sapStockBlocks = false;
  if (stockShortageIsBlocking && skuIac) {
    if (!Number.isFinite(cantNum) || cantNum <= 0) {
      sapStockBlocks = true;
      if (!stockInsufficient && !priceMismatch) {
        chunks.push(t('Cantidad OC inválida o sin unidades pedidas'));
      }
    } else if (Number.isFinite(stockNum)) {
      if (stockNum <= 0) {
        sapStockBlocks = true;
      } else if (cantNum > stockNum && stockNum / cantNum < 0.4) {
        sapStockBlocks = true;
      }
    }
  }

  if (sapPriceBlocks || sapStockBlocks) {
    validationStatus = 'error';
    validationMessage = '⚠ ' + chunks.join(' · ');
    return { validationStatus, validationMessage, sapPriceMismatch };
  }

  if (chunks.length) {
    validationStatus = 'warning';
    validationMessage = '⚠ ' + chunks.join(' · ');
  }

  return { validationStatus, validationMessage, sapPriceMismatch };
}

/**
 * Rellena precio SAP (`ItemPrices` según `sapPriceListId` del perfil) y stock (`InStock − Committed` en
 * la fila de bodega indicada) desde DTOs SAP y recalcula validación por línea.
 *
 * Si se pasa `brandDiscountPercent`, se aplica al `precioListaSap` para calcular el precio objetivo
 * (`precioListaSapConDescuento`); este valor se usa para la comparación y queda expuesto en cada
 * fila junto con el porcentaje aplicado, para que la UI los muestre en columnas separadas.
 * Si una fila trae `_headerDiscountPercent` &gt; 0 (vista previa OV → SAP), ese valor **tiene prioridad**
 * sobre `brandDiscountPercent` global (carga Excel sigue usando solo el global).
 *
 * @param {object[]} rows - filas de `mapAndValidateRows`; opcional `_bulkProfileId` por fila (p. ej. OV Ix Comercio desde API usa lista 3); opcional `_headerDiscountPercent` por fila (cabecera OV).
 * @param {Record<string, object|null>} itemCodeToDtoMap - una clave por código consultado; `null` = error / sin datos
 * @param {(key: string) => string} t
 * @param {string} [profileId]
 * @param {{ stockShortageIsBlocking?: boolean, sapWarehouseCode?: string, brandDiscountPercent?: number, sapPriceListId?: number }} [sapValidationOptions] — `stockShortageIsBlocking: true` solo «Procesar seleccionados» → SAP; `sapWarehouseCode` obligatorio (whs de sesión) para leer stock; `brandDiscountPercent` 0-100 (descuento por marca del partner SAP, default 0; anulado por fila si `_headerDiscountPercent` &gt; 0); `sapPriceListId` lista de precios SAP global del canal (flujo A), anulada por fila con `row._sapPriceListId` (flujo B). Sin lista válida → línea con SKU marcada como error.
 */
/**
 * Reescribe `skuIac` con el ItemCode SAP resuelto desde AlternateCatNum.
 * `resolution` está indexado por el Substitute normalizado:
 *   - `{ itemCode }` un ítem
 *   - `{ ambiguous: true }` más de un ítem
 *   - `null` el código no existe para ese cliente
 * Las filas sin entrada en el mapa se dejan como vienen (otro perfil, o la consulta aún no corrió).
 *
 * @param {object[]} rows
 * @param {Record<string, { itemCode?: string, ambiguous?: boolean }|null>} resolution
 */
export function applyCustomerCatalogResolution(rows, resolution) {
  if (!Array.isArray(rows) || !rows.length || !resolution) return rows;
  return rows.map((row) => {
    const sub = normalizeSapItemCodeKey(row.catalogSubstitute);
    if (!sub || !Object.prototype.hasOwnProperty.call(resolution, sub)) return row;
    const hit = resolution[sub];
    if (hit && hit.ambiguous) {
      return { ...row, skuIac: sub, _catalogAmbiguous: true };
    }
    if (hit && hit.itemCode) {
      return { ...row, skuIac: hit.itemCode, _catalogSubstitute: sub };
    }
    return { ...row, skuIac: sub };
  });
}

export function enrichBulkPreviewRowsWithSapItems(rows, itemCodeToDtoMap, t, profileId, sapValidationOptions) {
  if (!Array.isArray(rows) || !rows.length) return rows;
  const map = itemCodeToDtoMap && typeof itemCodeToDtoMap === 'object' ? itemCodeToDtoMap : {};
  const stockShortageIsBlocking = Boolean(sapValidationOptions?.stockShortageIsBlocking);
  const whsRaw = String(sapValidationOptions?.sapWarehouseCode ?? '').trim();
  const sapWhsCodeMissing = !whsRaw;
  /** Descuento por marca (opciones): número finito y > 0; si no, 0 (sin descuento). */
  const dPctRaw = Number(sapValidationOptions?.brandDiscountPercent);
  const safeBrandDiscountPct = Number.isFinite(dPctRaw) && dPctRaw > 0 ? dPctRaw : 0;

  return rows.map((row) => {
    const headerPctRaw = Number(row._headerDiscountPercent);
    const effectiveDiscountPct =
      Number.isFinite(headerPctRaw) && headerPctRaw > 0 ? headerPctRaw : safeBrandDiscountPct;
    const pid = String(row._bulkProfileId || profileId || BULK_FORMAT_DEFAULT_ID).trim() || BULK_FORMAT_DEFAULT_ID;
    const pdef = profileById(pid);
    /** CLP entero; soles con 2 decimales (mismo criterio que `computeBulkRowSapValidation`). */
    const rowCurrencyCode = getBulkFormatCurrency(pid).code;
    const skipStrictSap = pdef?.validation?.skipStrictSapPrice === true;
    /**
     * Lista de precios SAP a comparar = `PriceListNum` del cliente en SAP. Precedencia ESTRICTA:
     * `row._sapPriceListId` (por OV, flujo B) → `sapValidationOptions.sapPriceListId` (global, flujo A).
     * SIN fallback al perfil del Excel ni a `1`: si el cliente no tiene lista asignada queda `null`
     * y la línea con SKU se marca como error (`sapPriceListMissing`).
     */
    const isValidPriceListId = (v) =>
      v != null && Number.isFinite(Number(v)) && Number(v) > 0;
    const sapPriceListId = isValidPriceListId(row._sapPriceListId)
      ? Number(row._sapPriceListId)
      : isValidPriceListId(sapValidationOptions?.sapPriceListId)
        ? Number(sapValidationOptions.sapPriceListId)
        : null;
    /** Precio objetivo sin IGV: precio de lista × (1 − % descuento), para cualquier lista. */
    const applyBrandDiscount = (listPrice) =>
      listPrice != null && Number.isFinite(listPrice)
        ? roundMoneyForCurrency(listPrice * (1 - effectiveDiscountPct / 100), rowCurrencyCode)
        : null;

    const validationOptsBase = {
      skipStrictSap,
      profileId: pid,
      stockShortageIsBlocking,
      sessionWhsCode: whsRaw,
      sapWhsCodeMissing,
      brandDiscountPercent: effectiveDiscountPct
    };

    const code = normalizeSapItemCodeKey(row.skuIac);
    if (row._catalogAmbiguous && code) {
      const ambiguousMsg =
        t('El código de catálogo del cliente corresponde a más de un ítem SAP') + `: ${code}`;
      return {
        ...row,
        validationStatus: 'warning',
        validationMessage: '⚠ ' + ambiguousMsg,
        sapPriceMismatch: false
      };
    }
    if (!code) {
      let v = computeBulkRowSapValidation(row, t, {
        ...validationOptsBase,
        sapItemLookupFailed: false,
        sapWarehouseRowMissing: false
      });
      const cantForId = Number(row.cantOc);
      const needsLineItemUuid =
        stockShortageIsBlocking &&
        Number.isFinite(cantForId) &&
        cantForId > 0 &&
        !String(row._itemId ?? '').trim();
      if (needsLineItemUuid) {
        const missingMsg = t('Falta identificador de línea de la OV (item id).');
        v =
          v.validationStatus === 'error'
            ? {
                ...v,
                validationStatus: 'error',
                validationMessage: `${v.validationMessage} · ⚠ ${missingMsg}`
              }
            : {
                ...v,
                validationStatus: 'error',
                validationMessage: `⚠ ${missingMsg}`
              };
      }
      return {
        ...row,
        brandDiscountPercent: effectiveDiscountPct,
        precioListaSapConDescuento: applyBrandDiscount(row.precioListaSap),
        ...v
      };
    }

    if (!Object.prototype.hasOwnProperty.call(map, code)) {
      return {
        ...row,
        brandDiscountPercent: effectiveDiscountPct,
        precioListaSapConDescuento: applyBrandDiscount(row.precioListaSap)
      };
    }

    const dto = map[code];
    const sapItemLookupFailed = dto === null;
    const precioListaSap =
      sapPriceListId != null && dto && typeof dto === 'object' && !sapItemLookupFailed
        ? getSapItemPriceByPriceListId(dto, sapPriceListId)
        : null;
    const stockDispSap =
      !sapItemLookupFailed && whsRaw && dto && typeof dto === 'object'
        ? getSapItemStockForWarehouse(dto, whsRaw)
        : null;
    const sapWarehouseRowMissing = Boolean(
      whsRaw && !sapItemLookupFailed && dto && typeof dto === 'object' && stockDispSap === null
    );
    const itemCodeSap =
      dto && typeof dto === 'object' && !sapItemLookupFailed
        ? normalizeSapItemCodeKey(dto.ItemCode ?? dto.itemCode) || code
        : null;

    const precioListaSapConDescuento = applyBrandDiscount(precioListaSap);
    const merged = {
      ...row,
      precioListaSap,
      stockDispSap,
      itemCodeSap,
      precioListaSapConDescuento,
      brandDiscountPercent: effectiveDiscountPct
    };
    let v = computeBulkRowSapValidation(merged, t, {
      ...validationOptsBase,
      sapItemLookupFailed,
      sapWarehouseRowMissing,
      sapPriceListMissing: sapPriceListId == null
    });
    const cantForId = Number(row.cantOc ?? merged.cantOc);
    const needsLineItemUuid =
      stockShortageIsBlocking &&
      Number.isFinite(cantForId) &&
      cantForId > 0 &&
      !String(row._itemId ?? merged._itemId ?? '').trim();
    if (needsLineItemUuid) {
      const missingMsg = t('Falta identificador de línea de la OV (item id).');
      v =
        v.validationStatus === 'error'
          ? {
              ...v,
              validationStatus: 'error',
              validationMessage: `${v.validationMessage} · ⚠ ${missingMsg}`
            }
          : {
              ...v,
              validationStatus: 'error',
              validationMessage: `⚠ ${missingMsg}`
            };
    }
    return { ...merged, ...v };
  });
}

/**
 * @param {object[]} rawRows
 * @param {(key: string) => string} t - autoT
 * @param {{ profileId?: string, preambleMeta?: { nOc?: string, fechaOc?: string }, priceMap?: Map<string, number>|null, brandDiscountPercent?: number }} [options] - `priceMap` solo aplica a Falabella Chile (eOC): rellena `precioAcordado` cuando la fila del eOD no trae precio. `brandDiscountPercent` (solo Falabella Chile) se aplica al `COSTO_UNI` del eOC para que el precioAcordado mostrado coincida con el precio neto que persistirá el backend.
 */
export function mapAndValidateRows(rawRows, t, options = {}) {
  const profileId = options.profileId || BULK_FORMAT_DEFAULT_ID;
  const preambleMeta = options.preambleMeta && typeof options.preambleMeta === 'object' ? options.preambleMeta : {};
  const priceMap =
    options.priceMap instanceof Map && options.priceMap.size > 0 ? options.priceMap : null;
  const brandDiscountPctRaw = Number(options.brandDiscountPercent);
  const brandDiscountPct =
    Number.isFinite(brandDiscountPctRaw) && brandDiscountPctRaw > 0 ? brandDiscountPctRaw : 0;
  /** Solo aplica al rellenar `precioAcordado` desde el eOC en Falabella Chile (no al precio explícito del eOD). */
  const eocPriceFactor = brandDiscountPct > 0 ? 1 - brandDiscountPct / 100 : 1;
  const aliases = mergeAliasesForProfile(profileId);
  const pdef = profileById(profileId);
  const skipStrictSap = pdef?.validation?.skipStrictSapPrice === true;

  return rawRows.map((raw, idx) => {
    const normRow = normalizeRowKeys(raw);

    let nOc = String(pickField(normRow, aliases.nOc) || '');
    if (!nOc && preambleMeta.nOc) nOc = String(preambleMeta.nOc).trim();

    let tiendaDestino;
    let sucursalDestinoLinea = '';
    if (profileId === RIPLEY_PERU_PROFILE_ID) {
      const codEntrega = pickFieldExact(normRow, ['Cod Suc Entrega']);
      const sucEntrega = pickFieldExact(normRow, ['Suc Entrega']);
      tiendaDestino = combinePeruBranchLabel(codEntrega, sucEntrega);
      const codDestino = pickFieldExact(normRow, ['Cod Suc Destino']);
      const sucDestino = pickFieldExact(normRow, ['Suc Destino']);
      sucursalDestinoLinea = combinePeruBranchLabel(codDestino, sucDestino);
    } else if (profileId === OECHSLE_PERU_PROFILE_ID) {
      tiendaDestino = String(preambleMeta.enviarA || '').trim();
      sucursalDestinoLinea = String(pickFieldExact(normRow, ['Sucursal']) || '');
    } else {
      tiendaDestino = String(pickFieldExact(normRow, aliases.tiendaDestino) || '');
    }
    const skuCliente = String(pickField(normRow, aliases.skuCliente) || '');
    const descripcion = String(pickField(normRow, aliases.descripcion) || '');
    const cantOc = parseIntLoose(pickField(normRow, aliases.cantOc));
    let fechaOc = normalizeFechaOcDisplay(pickField(normRow, aliases.fechaOc));
    if (!fechaOc && preambleMeta.fechaOc) fechaOc = normalizeFechaOcDisplay(preambleMeta.fechaOc);
    let precioAcordado = parseMoneyNumber(pickField(normRow, aliases.precioAcordado));
    /**
     * Falabella Chile: el eOD nuevo ya no trae columna `precio` (la antigua sí). Cuando el precio
     * del eOD viene vacío o 0, se completa con `COSTO_UNI` del eOC vía `priceMap` (match por SKU).
     * Si el eOD trae precio, ese gana (retrocompatible con la versión anterior del archivo).
     */
    if (
      profileId === FALABELLA_CHILE_PROFILE_ID &&
      priceMap &&
      (precioAcordado == null || precioAcordado === 0)
    ) {
      const skuKey = String(skuCliente || '').trim();
      if (skuKey && priceMap.has(skuKey)) {
        const fromEoc = priceMap.get(skuKey);
        if (Number.isFinite(fromEoc) && fromEoc > 0) {
          precioAcordado =
            eocPriceFactor < 1 ? Math.round(fromEoc * eocPriceFactor * 100) / 100 : fromEoc;
        }
      }
    }
    /** Código ítem SAP: solo columna PC13 (variantes de cabecera); precio/stock vía `enrichBulkPreviewRowsWithSapItems`. */
    const skuIac = String(pickField(normRow, aliases.skuIac) || '').trim();
    const catalogSubstitute = String(pickField(normRow, aliases.catalogSubstitute) || '').trim();
    const precioListaSap = null;
    const stockDispSap = null;
    /** Precio unitario × cantidad (CLP entero; soles con 2 decimales); null si falta cantidad o precio. */
    const totalLinea =
      cantOc != null && precioAcordado != null && Number.isFinite(precioAcordado)
        ? roundMoneyForCurrency(cantOc * precioAcordado, getBulkFormatCurrency(profileId).code)
        : null;

    const baseRow = {
      precioAcordado,
      precioListaSap,
      stockDispSap,
      cantOc,
      skuIac
    };
    const { validationStatus, validationMessage, sapPriceMismatch } = computeBulkRowSapValidation(baseRow, t, {
      skipStrictSap,
      sapItemLookupFailed: false,
      profileId
    });

    return {
      id: `row-${idx + 1}`,
      rowIndex: idx + 1,
      nOc,
      tiendaDestino,
      sucursalDestinoLinea,
      skuCliente,
      skuIac,
      catalogSubstitute,
      descripcion,
      cantOc,
      fechaOc,
      precioAcordado,
      totalLinea,
      precioListaSap,
      stockDispSap,
      validationStatus,
      validationMessage,
      sapPriceMismatch
    };
  });
}

export function countByValidation(rows) {
  let ok = 0;
  let error = 0;
  let warning = 0;
  rows.forEach((r) => {
    if (r.validationStatus === 'error') error += 1;
    else if (r.validationStatus === 'warning') warning += 1;
    else ok += 1;
  });
  return { ok, error, warning };
}

/**
 * Agrupa filas de vista previa por **cliente / tienda destino** (misma columna que en lista OV: TDOMIN, TARICA…).
 * Valor tomado de cabeceras como `Cliente`, `Tienda destino`, etc. (ver `TIENDA_O_CLIENTE_ALIASES`).
 * Filas sin valor comparten el grupo “sin tienda”.
 *
 * @param {object[]} rows - ítems devueltos por `mapAndValidateRows`
 * @returns {object[]} Grupos con `id`, `clientLabel`, `detailCount`, `nOc`, `fechaOc`, `detailRows`, `validationStatus`, `validationMessage`, `warningLineCount`, `errorLineCount`
 */
export function groupBulkPreviewRowsByClient(rows) {
  if (!Array.isArray(rows) || !rows.length) return [];

  // Una entrada por valor distinto de tienda/cliente (proviene de `tiendaDestino` en cada fila mapeada).
  const map = new Map();
  for (const row of rows) {
    const raw = String(row.tiendaDestino ?? '').trim();
    const key = raw || '__sin_tienda__';
    if (!map.has(key)) {
      map.set(key, {
        clientKey: key,
        clientLabel: raw,
        detailRows: [],
        nOc: '',
        fechaOc: ''
      });
    }
    const g = map.get(key);
    g.detailRows.push(row);
    if (!g.nOc && row.nOc) g.nOc = String(row.nOc);
    if (!g.fechaOc && row.fechaOc) g.fechaOc = String(row.fechaOc);
  }

  return Array.from(map.values())
    .map((g) => {
      const detailCount = g.detailRows.length;
      let errorLineCount = 0;
      let warningLineCount = 0;
      for (const r of g.detailRows) {
        if (r.validationStatus === 'error') errorLineCount += 1;
        else if (r.validationStatus === 'warning') warningLineCount += 1;
      }

      let validationStatus = 'ok';
      let validationMessage = '';

      // El grupo hereda el peor estado de sus líneas (error > advertencia > ok).
      for (const r of g.detailRows) {
        if (r.validationStatus === 'error') {
          validationStatus = 'error';
          validationMessage = r.validationMessage;
          break;
        }
      }
      if (validationStatus !== 'error') {
        for (const r of g.detailRows) {
          if (r.validationStatus === 'warning') {
            validationStatus = 'warning';
            validationMessage = r.validationMessage;
            break;
          }
        }
      }
      if (validationStatus === 'ok') {
        validationMessage = g.detailRows[0]?.validationMessage || '';
      }

      return {
        id: `grp-${g.clientKey}`,
        clientKey: g.clientKey,
        clientLabel: g.clientLabel,
        detailCount,
        nOc: g.nOc,
        fechaOc: g.fechaOc,
        detailRows: g.detailRows,
        validationStatus,
        validationMessage,
        warningLineCount,
        errorLineCount
      };
    })
    .sort((a, b) => {
      const la = a.clientLabel || '\uffff';
      const lb = b.clientLabel || '\uffff';
      return la.localeCompare(lb, 'es', { sensitivity: 'base' });
    });
}
